// src/pages/MapPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
// Import Col from react-bootstrap
import { Container, Row, Col, Card, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkedAlt, faSpinner, faExclamationTriangle, faHome } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { getFirestore, collection, getDocs, doc, getDoc, query, where, documentId } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

// --- Configuration ---
const Maps_API_KEY_FROM_ENV = process.env.REACT_APP_MAP_API_KEY;
// Default center (Fuquay-Varina, NC - approximately)
const HARDCODED_DEFAULT_MAP_CENTER = { lat: 35.5857, lng: -78.8011 };
const DEFAULT_MAP_ZOOM = 11;
const db = getFirestore();
const FIRESTORE_IN_QUERY_LIMIT = 30; // Firestore 'in' query limit
const ORG_ICON_PATH = '/assets/church.svg'; // Path to custom SVG icon in public folder

function MapPage() {
    // --- State Variables ---
    const { userOrgId, loadingAuth, currentUser } = useAuth(); // Auth context
    const [groups, setGroups] = useState([]); // List of groups for the org
    const [selectedGroupId, setSelectedGroupId] = useState(''); // ID of the selected group
    const [membersData, setMembersData] = useState([]); // Member data for the selected group/org
    const [familiesData, setFamiliesData] = useState([]); // Family data with coordinates for map markers
    const [organizationDetails, setOrganizationDetails] = useState(null); // Org details (name, address, lat/lng)
    const [initialMapCenter, setInitialMapCenter] = useState(HARDCODED_DEFAULT_MAP_CENTER); // Center based on org or default
    const [mapCenter, setMapCenter] = useState(HARDCODED_DEFAULT_MAP_CENTER); // Current map center
    const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM); // Current map zoom level
    const [selectedFamily, setSelectedFamily] = useState(null); // Currently selected family for InfoWindow
    const [selectedOrganization, setSelectedOrganization] = useState(null); // Flag/data for showing Org InfoWindow
    const [loadingGroups, setLoadingGroups] = useState(true); // Loading state for groups
    const [loadingMembers, setLoadingMembers] = useState(false); // Loading state for members
    const [loadingFamilies, setLoadingFamilies] = useState(false); // Loading state for families
    const [error, setError] = useState(null); // Error messages

    // --- Google Maps API Loader ---
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: Maps_API_KEY_FROM_ENV,
        id: 'google-map-script'
    });

    // --- Effects ---

    // Effect 1: Fetch Organization Details and Set Initial Map Center/Zoom
    useEffect(() => {
        if (userOrgId) {
            const fetchOrganizationCenter = async () => {
                console.log("MapPage: Fetching organization details for orgId:", userOrgId);
                try {
                    const orgDocRef = doc(db, 'organization', userOrgId);
                    const orgDocSnap = await getDoc(orgDocRef);
                    if (orgDocSnap.exists()) {
                        const orgData = orgDocSnap.data();
                        // Store all organization data, using specific org field names from provided code
                        setOrganizationDetails({
                            id: orgDocSnap.id,
                            orgName: orgData.orgName || "Organization Location", // Use orgName
                            orgStreet: orgData.orgAddress, // Use orgAddress for street
                            orgCity: orgData.orgCity,
                            orgState: orgData.orgState,
                            orgZipcode: orgData.orgZip, // Use orgZip for zipcode
                            lat: orgData.lat,
                            lng: orgData.lng,
                            defaultZoom: orgData.defaultZoom,
                            ...orgData // Spread remaining data just in case
                        });

                        if (orgData.lat != null && orgData.lng != null) {
                            const newOrgCenter = { lat: orgData.lat, lng: orgData.lng };
                            console.log("MapPage: Setting initial map center to organization's location:", newOrgCenter);
                            setInitialMapCenter(newOrgCenter);
                            setMapCenter(newOrgCenter); // Initially center on org
                            setMapZoom(orgData.defaultZoom || DEFAULT_MAP_ZOOM); // Use org zoom or default
                        } else {
                            console.warn(`MapPage: Org doc ${userOrgId} found, but missing lat/lng. Using hardcoded default center.`);
                            setInitialMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                            setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                            setMapZoom(DEFAULT_MAP_ZOOM);
                        }
                    } else {
                        console.warn(`MapPage: Org doc ${userOrgId} not found. Using hardcoded default.`);
                        setOrganizationDetails({ orgName: "Default Location (Org Not Found)" }); // Use orgName
                        setInitialMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                        setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                        setMapZoom(DEFAULT_MAP_ZOOM);
                    }
                } catch (err) {
                    console.error("MapPage: Error fetching organization details:", err);
                    setError(`Failed to load organization details: ${err.message}`);
                    setOrganizationDetails({ orgName: "Default Location (Error)" }); // Use orgName
                    setInitialMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                    setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                    setMapZoom(DEFAULT_MAP_ZOOM);
                }
            };
            fetchOrganizationCenter();
        } else {
            // Handle case where userOrgId is not yet available (e.g., during initial auth loading)
            console.log("MapPage: userOrgId not available yet.");
            setInitialMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
            setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
            setMapZoom(DEFAULT_MAP_ZOOM);
            // Don't set organizationDetails here, let loading state handle UI
        }
    }, [userOrgId]); // Dependency: Run when userOrgId changes

    // Effect 2: Fetch Groups for the Organization
    useEffect(() => {
        if (loadingAuth || !currentUser || !userOrgId) {
            setLoadingGroups(loadingAuth || !userOrgId);
            if (!currentUser && !loadingAuth) setError("Please log in to view the map.");
            else if (!userOrgId && currentUser && !loadingAuth) setError("Organization details missing. Cannot fetch groups.");
            setGroups([]);
            return;
        }

        let isMounted = true;
        const fetchGroupsForOrg = async () => {
            console.log("MapPage: Fetching groups for orgId:", userOrgId);
            setLoadingGroups(true); setError(null); setGroups([]);
            try {
                const groupCollectionRef = collection(db, 'group');
                const q = query(groupCollectionRef, where('orgId', '==', userOrgId));
                const groupsSnapshot = await getDocs(q);
                if (!isMounted) return;

                const groupsList = groupsSnapshot.docs.map(d => ({
                    id: d.id,
                    name: d.data().groupName || `Group ${d.id.substring(0, 5)}...`,
                    ...d.data()
                }));

                console.log(`MapPage: Fetched ${groupsList.length} groups.`);
                setGroups(groupsList);
            } catch (err) {
                console.error("Error fetching groups:", err);
                if (isMounted) setError(`Failed to load groups. ${err.message}`);
            } finally {
                if (isMounted) setLoadingGroups(false);
            }
        };
        fetchGroupsForOrg();
        return () => { isMounted = false; };
    }, [userOrgId, loadingAuth, currentUser]);

    // Effect 3: Fetch Members based on Selected Group
    const fetchMembers = useCallback(async () => {
         if (!selectedGroupId || !userOrgId) {
            setMembersData([]);
            setLoadingMembers(false);
            return;
        }
        console.log(`MapPage: Fetching members for ${selectedGroupId === 'all' ? 'all groups' : `group ID ${selectedGroupId}`} in org ${userOrgId}`);
        setLoadingMembers(true); setError(null); setMembersData([]);
        try {
            const memberCollectionRef = collection(db, 'member');
            let memberDocs = [];
            if (selectedGroupId === 'all') {
                const q = query(memberCollectionRef, where('orgId', '==', userOrgId));
                const membersSnapshot = await getDocs(q);
                memberDocs = membersSnapshot.docs;
                console.log(`MapPage: Found ${memberDocs.length} total members for org.`);
            } else {
                const roleCollectionRef = collection(db, 'role');
                const rolesQuery = query(roleCollectionRef, where('groupId', '==', selectedGroupId), where('orgId', '==', userOrgId));
                const rolesSnapshot = await getDocs(rolesQuery);
                const memberIds = rolesSnapshot.docs.map(d => d.data().memberId).filter(Boolean);
                if (memberIds.length === 0) {
                    console.warn(`MapPage: No memberIds found via roles for group ID "${selectedGroupId}".`);
                    setLoadingMembers(false);
                    return;
                }
                console.log(`MapPage: Fetching ${memberIds.length} members by ID for group ${selectedGroupId}.`);
                for (let i = 0; i < memberIds.length; i += FIRESTORE_IN_QUERY_LIMIT) {
                    const batchIds = memberIds.slice(i, i + FIRESTORE_IN_QUERY_LIMIT);
                    const membersQuery = query(memberCollectionRef, where('orgId', '==', userOrgId), where(documentId(), 'in', batchIds));
                    const membersSnapshot = await getDocs(membersQuery);
                    memberDocs.push(...membersSnapshot.docs);
                }
                console.log(`MapPage: Fetched ${memberDocs.length} members for specific group.`);
            }
            const fetchedMembersData = memberDocs.map(d => ({ id: d.id, ...d.data() }));
            setMembersData(fetchedMembersData);
        } catch (err) {
            console.error("Error in fetchMembers:", err);
            setError(`Failed to load members. ${err.message}`);
            setMembersData([]);
        } finally {
            setLoadingMembers(false);
        }
    }, [selectedGroupId, userOrgId]);

    // Effect 4: Fetch Family Locations based on Fetched Members
    const fetchFamilies = useCallback(async () => {
        if (membersData.length === 0 || !userOrgId) {
            setFamiliesData([]);
            setLoadingFamilies(false);
            return;
        }
        console.log(`MapPage: Fetching families for ${membersData.length} members.`);
        setLoadingFamilies(true); setFamiliesData([]);
        try {
            const uniqueFamilyIds = membersData.reduce((acc, member) => {
                if (member.orgId === userOrgId && member.familyId && typeof member.familyId === 'string' && member.familyId.trim() !== '') {
                    acc.add(member.familyId.trim());
                }
                return acc;
            }, new Set());
            const familyIdsToFetch = Array.from(uniqueFamilyIds);
            if (familyIdsToFetch.length === 0) {
                console.log("MapPage: No valid unique familyIds found among members.");
                setLoadingFamilies(false);
                return;
            }
            console.log(`MapPage: Attempting to fetch ${familyIdsToFetch.length} unique families.`);
            let allValidFamilyData = [];
            let fetchErrorCount = 0, validationErrorCount = 0, notFoundCount = 0;
            for (const familyId of familyIdsToFetch) {
                try {
                    const familyDocRef = doc(db, 'family', familyId);
                    const familyDocSnap = await getDoc(familyDocRef);
                    if (familyDocSnap.exists()) {
                        const familyData = familyDocSnap.data();
                        if (familyData.orgId === userOrgId && familyData.lat != null && familyData.lng != null) {
                            allValidFamilyData.push({ id: familyDocSnap.id, ...familyData });
                        } else {
                            validationErrorCount++;
                            console.warn(`MapPage: Family ID ${familyId} fetched but failed validation (Org match: ${familyData.orgId === userOrgId}, LatLng: ${familyData.lat},${familyData.lng}).`);
                        }
                    } else {
                        notFoundCount++;
                        console.warn(`MapPage: Family document with ID ${familyId} not found.`);
                    }
                } catch (err) {
                    fetchErrorCount++;
                    console.error(`MapPage: Error fetching family ID ${familyId}: ${err.message}`);
                }
            }
            let summaryMessage = `MapPage: Processed ${familyIdsToFetch.length} family IDs. Found ${allValidFamilyData.length} valid families with coordinates.`;
            const issues = [];
            if (fetchErrorCount > 0) issues.push(`${fetchErrorCount} failed fetch`);
            if (validationErrorCount > 0) issues.push(`${validationErrorCount} failed validation`);
            if (notFoundCount > 0) issues.push(`${notFoundCount} not found`);
            if (issues.length > 0) summaryMessage += ` Issues: ${issues.join(', ')}.`;
            console.log(summaryMessage);
            setFamiliesData(allValidFamilyData);
            if (allValidFamilyData.length > 0 && selectedGroupId) {
                const firstFamily = allValidFamilyData[0];
                setMapCenter({ lat: firstFamily.lat, lng: firstFamily.lng });
                setMapZoom(allValidFamilyData.length > 1 ? 11 : 13);
            } else if (!selectedGroupId && organizationDetails?.lat && organizationDetails?.lng) {
                setMapCenter({ lat: organizationDetails.lat, lng: organizationDetails.lng });
                setMapZoom(organizationDetails.defaultZoom || DEFAULT_MAP_ZOOM);
            }
        } catch (err) {
            console.error("Error in overall fetchFamilies:", err);
            setError(`Failed to load family locations. ${err.message}`);
            setFamiliesData([]);
        } finally {
            setLoadingFamilies(false);
        }
    }, [membersData, userOrgId, organizationDetails, selectedGroupId]);

    // Effect 5: Trigger Fetching Based on Dependencies
    useEffect(() => { fetchMembers(); }, [fetchMembers]);
    useEffect(() => { fetchFamilies(); }, [fetchFamilies]);

    // --- Event Handlers ---
    const handleGroupChange = (event) => {
        const newSelectedGroupId = event.target.value;
        console.log("MapPage: Group selection changed to:", newSelectedGroupId);
        setSelectedGroupId(newSelectedGroupId);
        setSelectedFamily(null);
        setSelectedOrganization(null);
        setError(null);
        setFamiliesData([]);
        setMembersData([]);
        if (!newSelectedGroupId && organizationDetails?.lat && organizationDetails?.lng) {
            setMapCenter({ lat: organizationDetails.lat, lng: organizationDetails.lng });
            setMapZoom(organizationDetails.defaultZoom || DEFAULT_MAP_ZOOM);
        }
    };

    const handleMarkerClick = (type, data) => {
        if (type === 'family') {
            setSelectedOrganization(null);
            setSelectedFamily(data);
            setMapCenter({ lat: data.lat, lng: data.lng });
        } else if (type === 'organization') {
            setSelectedFamily(null);
            setSelectedOrganization(data);
            if (data.lat && data.lng) {
                setMapCenter({ lat: data.lat, lng: data.lng });
            }
        }
    };

    const handleMapClick = () => {
        setSelectedFamily(null);
        setSelectedOrganization(null);
    };

    // --- Render Logic ---
    const showInitialLoading = loadingAuth || (!organizationDetails && !error && userOrgId);
    if (showInitialLoading) {
        return <Container className="text-center mt-5"><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></Container>;
    }
    if (!currentUser && !loadingAuth) {
        return <Container className="mt-3"><Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view the map.</Alert></Container>;
    }
    if (!userOrgId && currentUser && !loadingAuth && !organizationDetails && !error) {
        return <Container className="mt-3"><Alert variant="warning">Organization details are not available for your account.</Alert></Container>;
    }

    // Render the Map Component
    const renderMapActual = () => {
        if (loadError) { return <Alert variant="danger" className="m-3">Error loading Google Maps script. Please check your API key and internet connection.</Alert>; }
        if (!isLoaded) { return <div className="text-center p-5"><Spinner animation="border" className="me-2" />Loading Map Interface...</div>; }

        const orgMarkerIcon = {
            url: ORG_ICON_PATH,
            scaledSize: new window.google.maps.Size(35, 35),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(17, 35),
        };

        const showDataLoadingIndicator = loadingMembers || loadingFamilies;

        // Use map height from the provided code: 60vh / 400px
        return (
            <div style={{ position: 'relative', height: '60vh', minHeight: '400px' }}>
                {showDataLoadingIndicator && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, backdropFilter: 'blur(2px)' }}>
                        <Spinner animation="border" role="status" className="me-2" /> Loading Locations...
                    </div>
                )}
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={mapZoom}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                    onClick={handleMapClick}
                >
                    {organizationDetails?.lat && organizationDetails?.lng && (
                        <Marker
                            key="organization-marker"
                            position={{ lat: organizationDetails.lat, lng: organizationDetails.lng }}
                            icon={orgMarkerIcon}
                            onClick={() => handleMarkerClick('organization', organizationDetails)}
                            zIndex={10}
                        />
                    )}
                    {!showDataLoadingIndicator && familiesData.map(family => (
                        <Marker
                            key={family.id}
                            position={{ lat: family.lat, lng: family.lng }}
                            onClick={() => handleMarkerClick('family', family)}
                        />
                    ))}
                    {selectedOrganization && organizationDetails?.lat && organizationDetails?.lng && (
                        <InfoWindow
                            position={{ lat: organizationDetails.lat, lng: organizationDetails.lng }}
                            onCloseClick={() => setSelectedOrganization(null)}
                        >
                            <div>
                                <h5 style={{ margin: '0 0 5px 0' }}>
                                    {selectedOrganization.orgName || 'Organization Location'}
                                </h5>
                                <p style={{ margin: 0, lineHeight: 1.3, fontSize: '0.9em' }}>
                                    {selectedOrganization.orgStreet && ( <>{selectedOrganization.orgStreet}<br /></> )}
                                    { (selectedOrganization.orgCity || selectedOrganization.orgState || selectedOrganization.orgZipcode) && (
                                        <>
                                            {selectedOrganization.orgCity || ''}
                                            {selectedOrganization.orgCity && selectedOrganization.orgState ? ', ' : ''}
                                            {selectedOrganization.orgState || ''}
                                            {selectedOrganization.orgZipcode ? ` ${selectedOrganization.orgZipcode}` : ''}
                                        </>
                                    )}
                                    {!selectedOrganization.orgStreet && !selectedOrganization.orgCity && !selectedOrganization.orgState && (
                                        <em>Address details not available.</em>
                                    )}
                                </p>
                            </div>
                        </InfoWindow>
                    )}
                    {selectedFamily && (
                        <InfoWindow
                            position={{ lat: selectedFamily.lat, lng: selectedFamily.lng }}
                            onCloseClick={() => setSelectedFamily(null)}
                        >
                            <div>
                                <h5 style={{ margin: '0 0 5px 0' }}>
                                    {selectedFamily.surname ? `${selectedFamily.surname} Family` : 'Selected Location'}
                                </h5>
                                <p style={{ margin: 0, lineHeight: 1.3, fontSize: '0.9em' }}>
                                    {selectedFamily.street && <>{selectedFamily.street}<br /></>}
                                    {(selectedFamily.city || selectedFamily.state || selectedFamily.zipcode) &&
                                        <>{selectedFamily.city ? `${selectedFamily.city}, ` : ''}
                                         {selectedFamily.state || ''} {selectedFamily.zipcode || ''}</>}
                                    {!selectedFamily.street && !selectedFamily.city && !selectedFamily.state && !selectedFamily.zipcode &&
                                        <em>No address available</em>}
                                </p>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </div>
        );
    };

    // --- Main Component Return ---
    return (
        <Container fluid className="mt-3">
            {/* MODIFIED: Breadcrumb and Group Selector Row */}
            <Row className="mx-1 mb-3 align-items-center justify-content-between">
                {/* Column 1: Breadcrumbs */}
                <Col xs={12} md="auto" className="mb-2 mb-md-0">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small mb-0">
                            <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                            <li className="breadcrumb-item active" aria-current="page">Map</li>
                        </ol>
                    </nav>
                </Col>

                {/* Column 2: Group Selector UI (Moved from Card Header) */}
                <Col xs={12} md="auto">
                    <div className="d-flex align-items-center">
                        <div className="me-2" style={{ color: 'black', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                            <FontAwesomeIcon icon={faHome} className="me-1" />
                            View Member Residences
                        </div>
                        <div style={{ minWidth: '250px' }}>
                            <Form.Select
                                aria-label="Select Group to View Member Residences"
                                value={selectedGroupId}
                                onChange={handleGroupChange}
                                disabled={loadingAuth || loadingGroups || loadingMembers || loadingFamilies || !userOrgId || !organizationDetails}
                                size="sm" // Optional: make dropdown slightly smaller
                            >
                                <option value="">-- Select a Group --</option>
                                <option value="all">All Members</option>
                                {groups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                            </Form.Select>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Map Card - Header Removed */}
            <Row className="justify-content-center mt-1"> {/* Reduced top margin */}
                <Col>
                    <Card className="shadow-sm">
                        {/* REMOVED Card.Header entirely */}
                        <Card.Body className="p-0">
                            {/* Global Error Display */}
                            {error && (
                                <Alert variant="danger" className="m-3 mb-0 rounded-0 border-start-0 border-end-0"> {/* Adjusted margin */}
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> {error}
                                </Alert>
                            )}
                            {/* Map Area or Placeholder */}
                            {userOrgId && organizationDetails && (!error || (error && (loadingFamilies || loadingMembers))) ? (
                                <>
                                    {renderMapActual()} {/* Renders the map with 60vh height */}
                                    {!loadingFamilies && !loadingMembers && familiesData.length === 0 && selectedGroupId && !error && (
                                        <div className="text-center p-3 text-muted small"> {/* Adjusted padding/size */}
                                            No family locations found with coordinates for the selected members.
                                        </div>
                                    )}
                                </>
                            ) : (
                                !error && !showInitialLoading &&
                                <div className="text-center p-5 text-muted">Map data cannot be displayed. Ensure organization details are loaded and valid.</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default MapPage;